/**
 * Which colour a tutor's lessons are drawn in, and how a second instrument
 * becomes a shade of it rather than a colour of its own.
 *
 * **Only the hue and the saturation of a stored colour are used.** How light
 * the block ends up is decided by the diary, not by the value somebody picked,
 * and that is deliberate: Everton blue is very dark and a mid orange is not,
 * so honouring the chosen lightness would make one tutor's week perfectly
 * readable and another's a row of navy bricks. Normalising it means every
 * tutor is equally legible and both themes can be handled by the stylesheet —
 * which is the only place that knows whether the page is light or dark.
 *
 * **Colour is never the only thing distinguishing two lessons.** Every block
 * carries a name in text — the student's on your own, the tutor's on a
 * colleague's — because roughly one man in twelve cannot separate two hues
 * reliably, and a diary that can only be read in colour is a diary some people
 * cannot read.
 */

/** A colour an owner can give somebody, as stored. */
export interface TutorColour {
  /** `#rrggbb`, lower case. What goes in the column. */
  value: string;
  label: string;
}

/**
 * The colours on offer.
 *
 * Sixteen, spread evenly around the wheel — about twenty-two degrees apart,
 * which is as fine as sixteen can be cut. That is close enough that two
 * neighbours are told apart by looking rather than at a glance, which is the
 * price of having twice as many, and the reason every block carries a name in
 * words as well.
 *
 * One colour belongs to at most one tutor. The rule is a partial unique index
 * on the table rather than a check in the route, because two owners picking at
 * once is exactly the case a check-then-write loses.
 */
export const TUTOR_COLOURS: readonly TutorColour[] = [
  { value: "#a3471f", label: "Vermilion" },
  { value: "#a3771f", label: "Amber" },
  { value: "#9ca31f", label: "Mustard" },
  { value: "#6ca31f", label: "Lime" },
  { value: "#39a31f", label: "Green" },
  { value: "#1fa335", label: "Emerald" },
  { value: "#1fa367", label: "Jade" },
  { value: "#1fa398", label: "Teal" },
  { value: "#1f7ba3", label: "Sky" },
  { value: "#003399", label: "Everton blue" },
  { value: "#261fa3", label: "Indigo" },
  { value: "#561fa3", label: "Violet" },
  { value: "#881fa3", label: "Purple" },
  { value: "#a31f8d", label: "Magenta" },
  { value: "#a31f5a", label: "Pink" },
  { value: "#a31f2a", label: "Crimson" },
] as const;

/**
 * Which colours are already spoken for, and by whom.
 *
 * Taken so that the picker can grey out a colour and say whose it is, rather
 * than letting somebody choose it and collect an error. The database is still
 * what enforces it — this only saves the click.
 */
export function takenColours(
  tutors: readonly { tutorProfileId: string | null; colour: string | null; displayName: string }[],
): Map<string, string> {
  const taken = new Map<string, string>();
  for (const tutor of tutors) {
    if (tutor.colour === null) continue;
    taken.set(tutor.colour.toLowerCase(), tutor.displayName);
  }
  return taken;
}

const HEX = /^#[0-9a-f]{6}$/;

/**
 * Whether a value may be stored as somebody's colour.
 *
 * Any six-digit hex, not only the eight above. The palette is what the picker
 * offers; the column holds a colour, and refusing one that arrived from an
 * older version of the list — or from somebody who typed it — would turn a
 * cosmetic choice into a failed save.
 */
export function isTutorColour(value: unknown): value is string {
  return typeof value === "string" && HEX.test(value.toLowerCase());
}

/** The label for a stored value, or the value itself where it is not one of ours. */
export function tutorColourLabel(value: string | null): string {
  if (value === null) return "No colour";
  const known = TUTOR_COLOURS.find((colour) => colour.value === value.toLowerCase());
  return known?.label ?? value;
}

/** A colour as the stylesheet wants it: a hue in degrees and a saturation. */
export interface Hsl {
  hue: number;
  saturation: number;
}

/**
 * The hue and saturation of a hex colour. Null for anything unreadable, which
 * draws in the house accent like a tutor with no colour set.
 *
 * Grey has no meaningful hue, so a colour with no saturation reports zero for
 * both and the stylesheet renders it as the grey it is.
 */
export function hueOf(value: string | null): Hsl | null {
  if (!isTutorColour(value)) return null;

  const hex = value.toLowerCase();
  const red = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const green = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const blue = Number.parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const span = max - min;

  if (span === 0) return { hue: 0, saturation: 0 };

  const lightness = (max + min) / 2;
  const saturation = span / (1 - Math.abs(2 * lightness - 1));

  let hue: number;
  if (max === red) hue = ((green - blue) / span) % 6;
  else if (max === green) hue = (blue - red) / span + 2;
  else hue = (red - green) / span + 4;

  hue *= 60;
  if (hue < 0) hue += 360;

  return {
    hue: Math.round(hue),
    saturation: Math.round(Math.min(1, saturation) * 100),
  };
}

/** How many shades of one tutor's colour the diary can tell apart. */
export const SHADES = 4;

/**
 * Which shade of their colour an instrument gets.
 *
 * Taken from the instrument's name rather than from its position in a list,
 * because a position would depend on what else is on screen — a tutor's piano
 * lessons would change shade when you moved to a week they also taught guitar
 * in, which reads as a bug and hides the one thing the shade is for.
 *
 * A tutor with one instrument always gets shade zero, which is the colour they
 * were given. Two instruments can collide onto the same shade; that costs a
 * distinction rather than making anything wrong, since what is taught is
 * written on the block in words as well.
 */
export function shadeFor(instrument: string | null): number {
  if (instrument === null || instrument.trim() === "") return 0;

  const name = instrument.trim().toLowerCase();
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    // Ordinary string hash, kept in 32 bits so it cannot drift between
    // engines: the shade has to be the same in every browser and in a test.
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }

  return Math.abs(hash) % SHADES;
}
